/**
 * FAQ content for the static (non-landing) routes.
 *
 * Kept in its own module so a page component can render its questions without
 * importing `meta.ts`, which pulls in the entire landing-page corpus (~50KB gz).
 * The builder and tool pages must not pay for that.
 *
 * `meta.ts` reads this same object to build the `FAQPage` structured data, so
 * the schema and the visible section are one source. Adding a question here
 * renders it and marks it up; removing one removes both.
 */

import { TOOL_COUNT } from '@/data/toolCount'

export const STATIC_FAQS: Record<string, { q: string; a: string }[]> = {
  '/tools': [
    {
      q: 'Which resume tool should I use first?',
      a: 'The ATS Health Checker. It scores the mechanical things that decide whether your resume is readable at all — headings, dates, contact details, formatting — and those are problems you can find and fix yourself. Fix those first, then move to the Job Description Matcher for a specific role.',
    },
    {
      q: `Are all ${TOOL_COUNT} tools really free?`,
      a: 'Yes, and there is no paid tier to upgrade to. Every tool, including PDF export, is free and unwatermarked. There is no account, no trial and no payment screen at any point.',
    },
    {
      q: 'Do I need to re-enter my resume for each tool?',
      a: 'No. A resume you build in the builder is stored in your browser, and every tool reads it automatically. A resume you paste or upload on a tool page stays in that tab’s memory, so it carries across the tool pages until you close or reload the tab.',
    },
    {
      q: 'Do these tools use AI?',
      a: 'No. Every suggestion comes from a rule that is written down and readable in the source, which is why the same resume always produces the same score and every finding cites a reason. The trade is real: rules miss nuance an AI would catch. In exchange nothing is invented, nothing is uploaded, and it runs offline.',
    },
    {
      q: 'Is my resume sent anywhere when I use these tools?',
      a: 'No. There is no server to send it to. Every tool is JavaScript running on your device and your resume is stored only in your own browser. Open DevTools, select the Network tab, and use every tool on the page — besides this site’s own files, the only requests are for the analytics and advertising services listed on the privacy page. None of them receives a word of your resume.',
    },
  ],

  '/': [
        {
          q: 'Is ATSResumeKit really free?',
          a: 'Yes. Every tool and the PDF export are free, with no watermark, and there is no paid tier to upgrade to. Many resume builders let you write for free and charge when you download. This one has no payment screen at any point.',
        },
        {
          q: 'Is my resume uploaded anywhere?',
          a: 'No. There is no server to upload it to. Every tool runs as JavaScript in your browser, and your resume is stored only in your own browser’s local storage. You can verify this yourself: open DevTools, go to the Network tab, and use every feature — besides this site’s own files, the only requests are for Google Analytics (no cookie until you accept the consent banner), Ahrefs Web Analytics (only if you accept), and Google’s advertising script on every page except the builder. None of them receives anything you typed.',
        },
        {
          q: 'Do I need to create an account?',
          a: 'No. There is no account system, no email capture and no login. Nothing is remembered about you between visits except the resume saved in your own browser, which you can delete by clearing site data.',
        },
        {
          q: 'Will my resume pass an applicant tracking system?',
          a: 'No tool can promise that, because every employer configures their system differently. What this does is remove the mechanical reasons for failure: single-column templates built to avoid common parsing problems, real selectable text in the exported PDF rather than an image, headings a parser recognises, and consistent date formats. The score is a checklist of removable risks, not a prediction.',
        },
        {
          q: 'Does this use AI to write my resume?',
          a: 'No, deliberately. Every suggestion comes from a rule somebody wrote and anybody can read, so the same resume always produces the same score and every finding cites a reason. AI writing tools invent employers, dates and metrics that you then have to defend in an interview. If you do want to use an assistant, the AI prompts tab in the builder gives it your real facts rather than letting it generate from nothing.',
        },
        {
          q: 'Can I use this for more than one job application?',
          a: 'Yes. You can keep multiple resumes, mark one as your master copy, and tailor a copy for a specific posting without touching the original. All of it stays in your browser.',
        },
        {
          q: 'What happens to my resume if I clear my browser data?',
          a: 'It is deleted, and there is no copy anywhere to restore it from — that is the trade-off of having no server. Export a JSON backup from the builder and keep it somewhere safe; you can re-import it in seconds, and it is the complete document in an open format.',
        },
  ],
  /*
   * No '/resume-builder' entry. That route used to be a real landing page with
   * these three questions visibly answered on it; it is now the client-only
   * app (see the merge note in scripts/prerender.ts), which renders no FAQ
   * section. `resolveMeta` would otherwise attach `FAQPage` structured data for
   * questions nothing on the page answers — exactly the visible-content
   * mismatch this file's own header comment warns against.
   */
  '/ats-resume-checker': [
          {
            q: 'Is this ATS resume checker really free?',
            a: 'Yes. Every feature is free and the source code is open. There is no paid tier, no watermark and no account.',
          },
          {
            q: 'Is my resume uploaded anywhere?',
            a: 'No. The entire analysis runs in your browser using JavaScript, including reading an uploaded PDF, DOCX or TXT file. A resume you paste or upload on the checker is kept in the tab’s memory only. A resume you build in the builder is stored in your own browser’s local storage. Neither is transmitted to a server.',
          },
          {
            q: 'How accurate is the ATS score?',
            a: 'The score comes from fixed rules about things parsers commonly depend on: standard section headings, readable dates, contact details that can be extracted, and real text rather than an image. It is not the score any employer’s system would give you. Employers use different systems with different settings, and no outside tool can reproduce them, so treat the score as a checklist of removable risks rather than a prediction. The methodology page lists every rule.',
          },
  ],
  /*
   * These two tool pages shipped with no FAQ at all — they were the only
   * transactional routes without one, which meant the two highest-intent queries
   * on the site ("how many keywords should I add", "will rewriting my bullets
   * look like AI") had no answer anywhere.
   */
  '/job-description-matcher': [
    {
      q: 'What match percentage should I be aiming for?',
      a: 'Around 60-75% is a realistic target, and 100% is a warning sign rather than a goal. A posting lists everything an ideal candidate might have, including items the hiring manager does not actually expect. Matching every one usually means you have pasted the posting back at them, which reads as keyword stuffing to a person and adds skills you may be asked to demonstrate.',
    },
    {
      q: 'Is adding keywords from the job description the same as keyword stuffing?',
      a: 'No, as long as each keyword sits inside a claim that is true. "Kubernetes" listed among your skills because you have run clusters is matching; the same word repeated in a hidden block, or added to a role where you never touched it, is stuffing. The difference is whether you could talk about it for two minutes in an interview.',
    },
    {
      q: 'Does the matcher understand that "K8s" and "Kubernetes" are the same thing?',
      a: 'Yes. It uses a hand-curated skill taxonomy with an alias table, so common abbreviations, spellings and product names resolve to the same skill. It also filters job-description boilerplate — without that step every posting’s top keyword is "experience" and the report is useless.',
    },
    {
      q: 'Is the job description I paste sent anywhere?',
      a: 'No. The matching runs entirely in your browser against a local dictionary, so the posting never leaves your device — which also means it works with no connection. Neither the posting nor your resume is transmitted at any point.',
    },
  ],
  '/resume-bullet-improver': [
    {
      q: 'Will a rewritten bullet look like it was written by AI?',
      a: 'No, because nothing here is generated. The tool identifies mechanical weaknesses — a weak opening verb, passive voice, a first-person pronoun, a missing number — and offers a rewrite only where the fix is purely structural, such as swapping "Responsible for managing" for "Managed". Anything that would require inventing a fact is flagged for you to fill in rather than filled in for you.',
    },
    {
      q: 'What makes a resume bullet weak?',
      a: 'Describing the duty instead of the outcome. "Responsible for the onboarding process" states what you were assigned; "Cut onboarding time from three weeks to five days by rewriting the checklist" states what changed because you were there. The most common specific faults are opening with a weak verb, passive construction, and having no number anywhere in the line.',
    },
    {
      q: 'Do all of my bullets need numbers in them?',
      a: 'No — aim for roughly half. A resume where every line carries a metric reads as padded, and some genuinely valuable work has no number attached to it. The tool tells you what proportion of your bullets are quantified and suggests which specific ones would benefit most, rather than demanding a figure for each.',
    },
    {
      q: 'What if I do not know the numbers for my past work?',
      a: 'Estimate honestly and say so, or use a different kind of measure. Team size, frequency, scope and duration are all quantities and are usually easy to recall — "reviewed roughly 40 tickets a week across three products" is stronger than no figure at all. The one thing not to do is invent a percentage, because that is the claim you will be asked about.',
    },
  ],
  '/how-it-works': [
    {
      q: 'Do I need to install anything?',
      a: 'No. It runs in the browser you already have, on desktop or phone, with nothing to download and no extension. After your first visit it also works offline, because the application caches itself — that follows from having no backend rather than being a feature that was added.',
    },
    {
      q: 'How long does it take to get a usable resume?',
      a: 'Around twenty minutes from an existing PDF, because the import fills most of it in and you are correcting rather than typing. From scratch, expect an hour of real work — the tools tell you what is missing as you go, so the time goes into deciding what to say rather than into formatting.',
    },
    {
      q: 'Can I use it on my phone?',
      a: 'Yes. The builder, the analysis and the PDF export all work on a phone, and the layout switches to one pane at a time rather than shrinking the desktop view. Importing a PDF works too, if you can reach the file from your phone’s file picker.',
    },
    {
      q: 'What happens if I close the tab in the middle?',
      a: 'Nothing is lost. Every change is saved to your browser’s local storage as you type, so reopening the site puts you back where you were. The only thing that clears it is clearing this site’s data — which is also how you delete everything, permanently and without asking anyone.',
    },
  ],
  '/contact': [
    {
      q: 'Is my resume sent when I use the contact form?',
      a: 'No. The form carries only the name, email and message you type into it, and it has no access to your resume at all — the document stays in your browser’s local storage. Sending a message is also the only moment this site contacts anyone else; simply loading a page sends nothing.',
    },
    {
      q: 'Can you recover a resume I lost?',
      a: 'No, and this is worth understanding before it happens. Your resume is stored only in your own browser, so it never reaches us and no copy exists for us to restore. Clearing site data, switching browser or using private browsing removes it permanently. Export a JSON backup from the builder — it takes one click.',
    },
    {
      q: 'Do I have to give an email address?',
      a: 'Yes. Name, email and message are all required. The address is only used to reply to you — it is not added to a list, and there is no list to add it to. If you would rather stay anonymous, say so in the message and we will read it and not write back.',
    },
    {
      q: 'What should I include when reporting a bug or a scoring rule I disagree with?',
      a: 'For a scoring rule: which one, and why you think it is wrong — the rules are the actual product, and every rule is meant to be defensible in one sentence, so if one is not, it should change. For a bug: three things — what you did, what happened, and what you expected instead.',
    },
    {
      q: 'Can you delete my personal data?',
      a: 'There is nothing on our side to delete. No account is created, no resume is transmitted, and we hold no database of visitors, so a deletion request against us has no target. Clearing your browser storage removes everything the product itself has ever held about you. Google Analytics is separate: it does associate an identifier with your visits at Google’s end (see the privacy page), and a deletion request for that goes through Google’s own account controls, not us.',
    },
  ],
  '/resume-builder': [
    {
      q: 'Is the ATS Resume Builder free?',
      a: 'Yes. Every template, every ATS check and the PDF export are free and unwatermarked, with no account and no paid tier to unlock.',
    },
    {
      q: 'Do I need an account?',
      a: 'No. There is no account system and no login. Your resume is saved automatically to this browser’s local storage as you work.',
    },
    {
      q: 'Is my resume uploaded anywhere?',
      a: 'No. The builder runs as JavaScript in your browser, and importing a PDF or a JSON file is parsed locally too — nothing is sent to a server at any point.',
    },
    {
      q: 'Can I export my resume?',
      a: 'Yes, two ways: as a PDF with real, selectable text rather than a flattened image, or as a JSON file you can use to back up your data or re-import it later.',
    },
    {
      q: 'Does the builder check ATS-related formatting?',
      a: 'Yes. It scores your resume across six categories — contact and identity, section structure, ATS-safe formatting, content quality, keyword coverage and length — and explains the reasoning behind each finding.',
    },
  ],
}
